/**
 * used when multiple parents are possible for a node. works by adding uuids to the end of values.
 */
import React, { useEffect } from 'react'
import type { TreeSelectOption } from './AntTreeSelect'
import { length, map, pathOr, pipe, uniq } from 'remeda'

const UUID_LENGTH = 36

export function getConfigForMultiParents({
    onChange,
    flattenedData,
    value,
    valueFieldName,
}: {
    flattenedData: TreeSelectOption[]
    onChange: (value: any, labelList: React.ReactNode[], extra: any) => void
    value: string[]
    valueFieldName: string
}) {
    useEffect(() => {
        resetTagLabels()
        return () => {
            resetTagLabels()
        }
    }, [value])

    let previousValues = value.flatMap(getAllValuesWithUUIDFromUUIDlessValue)
    return {
        /** keeps emitted values extra uuid free.
         * removes or adds all of the uuid postfixed values based on which is the user's intent by comparing it to previous values. */
        onChange: (newValues: string[] = []) => {
            const getValuesToEmit = (withUUIDs: string[]): string[] => uniq(withUUIDs.map(removeUUIDFromEnd))
            const newValuesToBeAdded = getValuesToEmit(newValues.filter((val) => !previousValues.includes(val)))
            const valuesToBeRemoved = getValuesToEmit(previousValues.filter((val) => !newValues.includes(val)))

            const valuesToEmit = uniq([
                ...getValuesToEmit(newValues).filter((val) => !valuesToBeRemoved.includes(val)),
                ...newValuesToBeAdded,
            ])
            previousValues = valuesToEmit.flatMap(getAllValuesWithUUIDFromUUIDlessValue)
            onChange(valuesToEmit, null, null)
        },
        value: value ? value.flatMap(getAllValuesWithUUIDFromUUIDlessValue) : [],
        maxTagPlaceholder: (omittedValues: Array<{ value: string; label: string }>) => {
            const omittedValuesLength = pipe(
                omittedValues ?? [],
                map(pathOr(['value'], '')),
                map(removeUUIDFromEnd),
                uniq(),
                length()
            )
            return omittedValuesLength ? <span>+ {omittedValuesLength}</span> : null
        },
        maxTagCount: 'responsive' as const,
    }

    function getAllValuesWithUUIDFromUUIDlessValue(uuidLessPropValue: string): string[] {
        return (
            flattenedData
                .map((node) => node[valueFieldName])
                .filter((valWithUUID) => removeUUIDFromEnd(valWithUUID) === uuidLessPropValue) ?? [uuidLessPropValue]
        )
    }
}
const tagValues = new Set<string>()

export async function hasTagNotBeenRendered(tagUUIDValue: string) {
    let hasTagNotBeenRendered: boolean
    await navigator.locks.request('tree-select-tag-unique-labels', async () => {
        const uuidlessValue = removeUUIDFromEnd(tagUUIDValue)
        hasTagNotBeenRendered = !tagValues.has(uuidlessValue)
        tagValues.add(uuidlessValue)
    })
    return hasTagNotBeenRendered
}

export function removeUUIDFromEnd(value: string) {
    return value.slice(0, -1 * UUID_LENGTH)
}

function resetTagLabels() {
    tagValues.clear()
}
